import { useState, useEffect } from "react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePropertySharing } from "@/hooks/usePropertySharing";
import { Loader2, Plus, Mail, X, Trash2, Shield, User, Copy } from "lucide-react";
import { toast } from "sonner";
import GlassCard from "./GlassCard";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const SharePropertySheet = ({ open, onOpenChange, property }) => {
    const {
        members,
        invites,
        loading,
        fetchSharingInfo,
        inviteUser,
        revokeInvite,
        removeMember
    } = usePropertySharing(property?.id);

    const [inviteEmail, setInviteEmail] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (open && property) {
            fetchSharingInfo();
        }
    }, [open, property, fetchSharingInfo]);

    const handleInvite = async () => {
        if (!inviteEmail) return;
        setSending(true);
        const success = await inviteUser(inviteEmail);
        setSending(false);
        if (success) setInviteEmail("");
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="bottom" onOpenAutoFocus={(e) => e.preventDefault()} className="max-h-[85vh] rounded-t-3xl">
                <SheetHeader className="pb-6">
                    <SheetTitle className="text-xl font-bold text-foreground">
                        Share Access
                    </SheetTitle>
                    <SheetDescription>
                        Manage who has access to <span className="font-medium text-foreground">{property?.name}</span>
                    </SheetDescription>
                </SheetHeader>

                <div className="space-y-6">
                    {/* Invite Form */}
                    <div className="space-y-3">
                        <Label>Invite new member</Label>
                        <div className="flex gap-2">
                            <Input
                                placeholder="Enter email address"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                className="rounded-xl"
                            />
                            <Button
                                onClick={handleInvite}
                                disabled={!inviteEmail || sending}
                                className="rounded-xl"
                            >
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invite"}
                            </Button>
                        </div>
                        <div className="pt-2">
                            <Button
                                variant="outline"
                                className="w-full rounded-xl gap-2 text-muted-foreground hover:text-primary"
                                onClick={() => {
                                    const link = `${window.location.origin}/auth?mode=signup&email=${inviteEmail}`;
                                    navigator.clipboard.writeText(link);
                                    toast.success("Invite link copied to clipboard");
                                }}
                            >
                                <Copy className="w-4 h-4" />
                                Copy Invite Link
                            </Button>
                        </div>
                    </div>

                    {/* Pending Invites */}
                    {invites.length > 0 && (
                        <div className="space-y-3">
                            <Label className="text-muted-foreground text-xs uppercase tracking-wider">Pending Invites</Label>
                            {invites.map((invite) => (
                                <GlassCard key={invite.id} className="p-3 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                                            <Mail className="w-4 h-4 text-amber-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium">{invite.invitee_email}</p>
                                            <p className="text-xs text-muted-foreground">Sent {new Date(invite.created_at).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                        onClick={() => revokeInvite(invite.id)}
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </GlassCard>
                            ))}
                        </div>
                    )}

                    {/* Members List */}
                    <div className="space-y-3">
                        <Label className="text-muted-foreground text-xs uppercase tracking-wider">Members</Label>
                        {loading ? (
                            <div className="flex justify-center py-4">
                                <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
                            </div>
                        ) : members.length === 0 ? (
                            <div className="text-center py-6 text-muted-foreground text-sm bg-muted/30 rounded-xl border border-dashed border-muted">
                                No other members yet
                            </div>
                        ) : (
                            members.map((member) => (
                                <GlassCard key={member.id} className="p-3 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Avatar className="w-8 h-8 border border-border">
                                            <AvatarFallback>
                                                {member.profiles?.full_name?.[0] || <User className="w-4 h-4" />}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <p className="text-sm font-medium">{member.profiles?.full_name || "Unknown User"}</p>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-xs text-muted-foreground capitalize">{member.role}</span>
                                                {member.role === 'admin' && <Shield className="w-3 h-3 text-primary" />}
                                                {/* If this is the invitee logic, we need to know who is owner. RLS handles it. */}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Only show Remove if NOT myself (usually owner can't remove themselves this way, or can they?) and NOT owner? */}
                                    {/* Logic: If I am the viewer (owner), I can remove anyone except myself. */}
                                    {/* Ideally we check current user ID */}
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                        onClick={() => removeMember(member.id)}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </GlassCard>
                            ))
                        )}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default SharePropertySheet;
